/**
 * Format a campaign period the way the mockup does:
 *   same month  → "24–30 Apr 2026"
 *   same year   → "28 Mar – 02 Apr 2026"
 *   cross year  → "28 Dec 2025 – 02 Jan 2026"
 *   no dates    → "—"
 *
 * Uses Intl with the `en-US` calendar formatting (month abbreviation), which is
 * locale-stable for the table column. Day is zero-padded to match the mockup.
 */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const EN_DASH = "–";

function parts(iso: string): { d: number; m: number; y: number } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return { d: date.getUTCDate(), m: date.getUTCMonth(), y: date.getUTCFullYear() };
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function formatPeriod(start?: string | null, end?: string | null): string {
  if (!start && !end) return "—";

  const s = start ? parts(start) : null;
  const e = end ? parts(end) : null;

  if (s && e) {
    if (s.y === e.y && s.m === e.m) {
      // 24–30 Apr 2026
      return `${pad(s.d)}${EN_DASH}${pad(e.d)} ${MONTHS[s.m]} ${s.y}`;
    }
    if (s.y === e.y) {
      // 28 Mar – 02 Apr 2026
      return `${pad(s.d)} ${MONTHS[s.m]} ${EN_DASH} ${pad(e.d)} ${MONTHS[e.m]} ${e.y}`;
    }
    // 28 Dec 2025 – 02 Jan 2026
    return `${pad(s.d)} ${MONTHS[s.m]} ${s.y} ${EN_DASH} ${pad(e.d)} ${MONTHS[e.m]} ${e.y}`;
  }

  const one = s ?? e;
  if (one) return `${pad(one.d)} ${MONTHS[one.m]} ${one.y}`;
  return "—";
}
